import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiError, SUB_STATE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Check, Loader2, MessageCircle, ShieldCheck, X, Crown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function Subscription() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["subscription-status"], queryFn: () => api.get("/subscription/status").then((r) => r.data) });
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: () => api.get("/plans").then((r) => r.data) });
  const { data: whatsapp } = useQuery({ queryKey: ["whatsapp"], queryFn: () => api.get("/whatsapp").then((r) => r.data) });
  const [busy, setBusy] = React.useState(null);
  const [paytr, setPaytr] = React.useState(null); // { token, oid, planName }
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = React.useState(searchParams.get("billing") === "yearly" ? "yearly" : "monthly"); // monthly | yearly

  const cancelSub = async () => {
    if (!window.confirm("Aboneliğinizi iptal etmek istiyor musunuz? Mevcut dönem sonuna kadar aktif kalır, sonraki dönem tahsil edilmez.")) return;
    try {
      await api.post("/subscription/cancel");
      qc.invalidateQueries({ queryKey: ["subscription-status"] });
      toast.success("Aboneliğiniz iptal edildi. Dönem sonuna kadar aktif.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const startPaytr = async (id, name) => {
    setBusy(id);
    try {
      const { data } = await api.post("/subscription/paytr/token", { plan_id: id });
      setPaytr({ token: data.iframe_token, oid: data.merchant_oid, planName: name });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const toggleWhatsApp = async (enabled) => {
    try {
      await api.post("/whatsapp/toggle", { enabled });
      qc.invalidateQueries({ queryKey: ["whatsapp"] });
      toast.success(enabled ? "WhatsApp bildirimleri açıldı" : "WhatsApp bildirimleri kapatıldı");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Load PayTR iframe resizer once the payment iframe is shown
  useEffect(() => {
    if (!paytr) return;
    const s = document.createElement("script");
    s.src = "https://www.paytr.com/js/iframeResizer.min.js";
    s.async = true;
    s.onload = () => { try { window.iFrameResize({}, "#paytr-iframe"); } catch (e) { /* noop */ } };
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch (e) { /* noop */ } };
  }, [paytr]);

  // Poll authoritative order status (updated only by verified PayTR callback)
  useEffect(() => {
    if (!paytr) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/subscription/order/${paytr.oid}`);
        if (data.status === "paid") {
          clearInterval(t);
          setPaytr(null);
          qc.invalidateQueries({ queryKey: ["subscription-status"] });
          qc.invalidateQueries({ queryKey: ["business"] });
          toast.success("Ödeme alındı, aboneliğiniz etkinleştirildi");
        } else if (data.status === "failed" || data.status === "token_error") {
          clearInterval(t);
          setPaytr(null);
          toast.error("Ödeme tamamlanamadı, lütfen tekrar deneyin");
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [paytr, qc]);

  if (!status || !plans)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const st = SUB_STATE[status.subscription_state] || SUB_STATE.free;
  const currentPlanId = status.plan?.id;
  const isPro = status.subscription_state === "active";

  const PRO_BENEFITS = [
    "Ayda 500 WhatsApp bildirimi",
    "Otomatik randevu hatırlatmaları",
    "Personel yönetimi & personel bazlı takvim",
    "Randevu, müşteri ve kazanç raporları",
    "Kapak görseli & markana özel tema",
    "Öncelikli e-posta desteği",
  ];

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <h1 className="text-3xl font-bold">Abonelik</h1>
        <p className="text-muted-foreground mt-1">Planınızı yönetin ve WhatsApp paketleri ekleyin</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground">Mevcut durum</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={cn("text-xs font-semibold px-2.5 py-1 rounded-full border", st.cls)}>{st.label}</span>
            {status.plan && <span className="font-semibold">{status.plan.name}</span>}
          </div>
        </div>
        <div className="text-right text-sm">
          {status.subscription_state === "active" && <p className="text-muted-foreground">Kalan: <b>{status.subscription_days_left} gün</b></p>}
          {status.subscription_state === "free" && <p className="text-muted-foreground">Ömür boyu ücretsiz · temel özellikler açık</p>}
          {status.subscription_state === "suspended" && <p className="text-red-600 font-medium">Hesabınız askıya alındı</p>}
        </div>
      </div>

      {whatsapp && (
        <section
          className="rounded-xl border border-border bg-card p-5"
          data-testid="subscription-whatsapp-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
                <MessageCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-semibold">WhatsApp Bildirimleri</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Kalan hakkınız: <b data-testid="subscription-wa-credits">{whatsapp.wa_credits}</b> kontör
                </p>
              </div>
            </div>
            <Switch
              checked={whatsapp.enabled}
              onCheckedChange={toggleWhatsApp}
              data-testid="subscription-whatsapp-toggle"
            />
          </div>
          <p className="mt-4 border-t border-border pt-4 text-sm text-muted-foreground">
            Rezervasyon onayı ve hatırlatma mesajları bu ayardan yönetilir. Ek kontör paketleri aşağıdadır.
          </p>
        </section>
      )}

      {status.subscription_state === "active" && (
        status.subscription_cancelled ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="sub-cancelled-note">
            Aboneliğiniz iptal edildi. Mevcut dönem sonuna kadar (<b>{status.subscription_days_left} gün</b>) hizmet devam eder; sonraki dönem tahsil edilmez. Dilediğinizde tekrar plan satın alabilirsiniz.
          </div>
        ) : (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" className="rounded-lg text-destructive border-destructive/40 hover:bg-destructive/5" onClick={cancelSub} data-testid="cancel-subscription-btn">
              Aboneliği İptal Et
            </Button>
          </div>
        )
      )}

      {!isPro && (
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/40 p-6" data-testid="pro-benefits">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-5 h-5 text-primary" />
            <h2 className="font-display font-bold text-lg">Pro ile neler kazanırsınız?</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2">
            {PRO_BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> <span>{b}</span></li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold">{isPro ? "Abonelik Planı" : "Pro'ya Geçin"}</h2>
          <div className="inline-flex items-center rounded-full border border-border bg-card p-1" data-testid="billing-toggle">
            <button onClick={() => setBilling("monthly")} data-testid="billing-monthly"
              className={cn("px-4 py-1.5 rounded-full text-sm font-medium transition-colors", billing === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>Aylık</button>
            <button onClick={() => setBilling("yearly")} data-testid="billing-yearly"
              className={cn("px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5", billing === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
              Yıllık <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded-full", billing === "yearly" ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700")}>2 AY BEDAVA</span>
            </button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
          {plans.subscriptions.filter((p) => (p.interval || "monthly") === billing).map((p) => {
            const current = p.id === currentPlanId;
            const yearly = (p.interval || "monthly") === "yearly";
            return (
              <div key={p.id} className={cn("rounded-2xl border bg-card p-6 flex flex-col relative",
                p.is_popular ? "border-primary ring-2 ring-primary/20" : "border-border")} data-testid="plan-card">
                {p.is_popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                    En Popüler
                  </span>
                )}
                <h3 className="text-lg font-semibold">{p.name}</h3>
                <p className="mt-2"><span className="font-display text-3xl font-bold">{money(p.price)}</span><span className="text-muted-foreground text-sm"> / {yearly ? "yıl" : "ay"}</span></p>
                {yearly && <p className="text-xs text-emerald-600 font-semibold mt-1">🎁 2 ay bedava · ayda ~{money(Math.round(p.price / 12))}</p>}
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button className="w-full rounded-lg mt-6" disabled={current || busy === p.id}
                  variant={current ? "outline" : "default"} onClick={() => startPaytr(p.id, p.name)} data-testid="select-plan-btn">
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : current ? "Mevcut Planınız" : "Satın Al"}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5" /> Tüm fiyatlara KDV dahildir. Ödemeler PayTR güvenli altyapısı ile alınır; kart bilgileriniz saklanmaz.
        </p>
      </div>

      {plans.whatsapp_addons.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">WhatsApp Ek Paketleri</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {plans.whatsapp_addons.map((a) => (
              <div key={a.id} className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4" data-testid="addon-card">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-2xl font-bold">{money(a.price)}</p>
                  <p className="text-sm text-muted-foreground">{a.whatsapp_quota.toLocaleString("tr-TR")} ek WhatsApp bildirimi</p>
                </div>
                <Button size="sm" className="rounded-lg" disabled={busy === a.id} onClick={() => startPaytr(a.id, a.name)} data-testid="buy-addon-btn">
                  {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Satın Al"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {paytr && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-3 overflow-auto" data-testid="paytr-modal">
          <div className="bg-card rounded-2xl w-full max-w-lg my-6 p-4 relative">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Güvenli Ödeme — {paytr.planName}</h3>
              <button onClick={() => setPaytr(null)} data-testid="paytr-close-btn" className="text-muted-foreground hover:text-foreground p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              id="paytr-iframe"
              title="PayTR Ödeme"
              src={`https://www.paytr.com/odeme/guvenli/${paytr.token}`}
              frameBorder="0"
              style={{ width: "100%", minHeight: 520, border: 0 }}
              data-testid="paytr-iframe"
            />
            <p className="text-xs text-muted-foreground mt-2 text-center">Ödeme tamamlandığında bu pencere otomatik kapanır.</p>
          </div>
        </div>
      )}
    </div>
  );
}
